import React from 'react';
import { Link } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import PropTypes, { bool, func, string } from 'prop-types';

const buttonStyles = {
  alignItems: 'center',
  borderRadius: 18,
  boxShadow: 'none',
  display: 'flex',
  height: 32,
  justifyContent: 'center',
  minWidth: 112,
  padding: '2px 16px 0',
  textDecoration: 'none',
  textTransform: 'none',
  whiteSpace: 'nowrap',
};

const ContainedLink = styled(Link, {
  shouldForwardProp: prop => !['disableAction'].includes(prop),
})(({ disableAction, theme }) => ({
  [theme.breakpoints.up('xs')]: {
    flex: 'initial',
  },
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
  },
  ...buttonStyles,
  backgroundColor: disableAction ? theme.palette.graphic.light : theme.palette.secondary.main,
  boxShadow: disableAction ? theme.shadows[1] : 'none',
  color: disableAction ? theme.palette.text.disabled : theme.palette.surface.white,
  flex: 1,
  pointerEvents: disableAction ? 'none' : 'initial',
}));

const OutlinedLink = styled(Link)(({ theme }) => ({
  [theme.breakpoints.up('xs')]: {
    flex: 'initial',
  },
  '&:disabled': {
    backgroundColor: theme.palette.surface.white,
    border: 'none',
  },
  '&:hover': {
    backgroundColor: theme.palette.secondary.light,
  },
  ...buttonStyles,
  border: `1px solid ${theme.palette.secondary.main}`,
  color: theme.palette.secondary.main,
  flex: 1,
  marginRight: 16,
}));

const ActionBar = ({
  backLabel,
  backLink,
  continueLabel,
  continueLink,
  disableAction,
  onBack,
  onContinue,
  showContinueButton,
  endIcon,
  startIcon,
}) => {
  return (
    <Stack
      alignItems="center"
      direction="row"
      sx={{
        backgroundColor: 'surface.white',
        bottom: 0,
        boxShadow: 0,
        height: 80,
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        justifyContent: { xs: 'flex-end', lg: 'flex-end' },
        position: 'fixed',
        px: 3,
        top: 'auto',
        width: 1,
        zIndex: 1200,
      }}
    >
      <OutlinedLink onClick={onBack} replace={false} to={backLink}>
        {startIcon}
        {backLabel}
      </OutlinedLink>
      {showContinueButton && (
        <ContainedLink
          disableAction={disableAction}
          onClick={onContinue}
          replace={false}
          to={continueLink}
        >
          <Stack alignItems="center" direction="row" spacing={1}>
            {continueLabel}
            {endIcon}
          </Stack>
        </ContainedLink>
      )}
    </Stack>
  );
};

ActionBar.propTypes = {
  backLabel: string,
  backLink: string,
  continueLabel: string,
  continueLink: string,
  disableAction: bool,
  endIcon: PropTypes.node,
  onBack: func,
  onContinue: func,
  showContinueButton: bool,
  startIcon: PropTypes.node,
};

ActionBar.defaultProps = {
  backLabel: 'Back',
  backLink: '.',
  continueLabel: 'Continue',
  continueLink: '.',
  disableAction: false,
  endIcon: undefined,
  onBack: undefined,
  onContinue: undefined,
  showContinueButton: true,
  startIcon: undefined,
};

export default ActionBar;
